import { useMemo } from 'react'

import {
  type SettingsSectionMenuOptions,
  UserSettingsMenuNames
} from 'components/rbac-settings/settings-utils'
import useAuth from 'hooks/use-auth'
import { isPrivateEdition } from 'utils/app-config'
import {
  apiKeysGet,
  companiesIdGet,
  companiesIdUsersGet,
  externalIntegrationsGet,
  usersAuthMfaInfoGet,
  usersAuthPasswordPut
} from 'utils/endpoints-access'

function getUserSettingsMenuOptions({
  canEditPassword,
  canReadAllUsers,
  canReadApiKeys,
  canReadCompany,
  canReadExternalIntegrations,
  canReadMfaAuthInfo
}: {
  canEditPassword: boolean
  canReadAllUsers: boolean
  canReadApiKeys: boolean
  canReadCompany: boolean
  canReadExternalIntegrations: boolean
  canReadMfaAuthInfo: boolean
}): SettingsSectionMenuOptions {
  return {
    icon: 'solidUser',
    items: [
      {
        name: UserSettingsMenuNames.Details,
        url: '/settings/profile/details',
        enabled: canReadAllUsers && canReadCompany // this page isn't useful unless they have both of these privileges
      },
      {
        name: UserSettingsMenuNames.Authentication,
        url: '/settings/profile/authentication',
        enabled: canEditPassword || canReadMfaAuthInfo
      },
      {
        name: UserSettingsMenuNames.APIKeys,
        url: '/settings/profile/api-keys',
        enabled: canReadApiKeys
      },
      {
        name: UserSettingsMenuNames.Integrations,
        url: '/settings/profile/integrations',
        enabled: canReadExternalIntegrations
      }
    ].filter((item) => item.enabled),
    sectionName: 'User'
  }
}

export function useGetUserSettingsMenuOptions() {
  const { hasEndpointAccess, currentTeamId } = useAuth()

  return useMemo(() => {
    // Details
    const canReadAllUsers = hasEndpointAccess(companiesIdUsersGet)
    const canReadCompany = hasEndpointAccess(companiesIdGet)

    // Authentication
    const canEditPassword = hasEndpointAccess(usersAuthPasswordPut)
    const canReadMfaAuthInfo = hasEndpointAccess(usersAuthMfaInfoGet)

    // API Keys
    const canReadApiKeys = hasEndpointAccess(apiKeysGet)

    // Integrations
    const canReadExternalIntegrations =
      !isPrivateEdition() &&
      hasEndpointAccess(externalIntegrationsGet, { teamId: currentTeamId })

    return getUserSettingsMenuOptions({
      canReadCompany,
      canReadAllUsers,
      canEditPassword,
      canReadMfaAuthInfo,
      canReadApiKeys,
      canReadExternalIntegrations
    })
  }, [currentTeamId, hasEndpointAccess])
}
